import { WizIconModule } from './components/controls/wiz-icon/wiz-icon.module';
import { WizAgGridModule } from './components/controls/wiz-ag-grid/wiz-ag-grid.module';
import { WizTabModule } from './components/tab/wiz-tab.module';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ComponentFactoryResolver,
  Injector,
  NgModule,
  RendererFactory2,
} from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { WizButtonModule } from './components/controls/button/wiz-button.module';
import { WizDateModule } from './components/controls/date/wiz-date.module';
import { WizDropdownModule } from './components/controls/wiz-dropdown/wiz-dropdown.module';
import { WizImageModule } from './components/controls/wiz-image/wiz-image.module';
import { WizLabelModule } from './components/controls/wiz-label/wiz-label.module';
import { WizListModule } from './components/controls/wiz-list/wiz-list.module';
import { WizOverlayContainer } from './components/core/overlay/wiz-overlay-container';
import { WizLayoutModule } from './components/layouts/wiz-layout.module';
import { WizTreeModule } from './components/controls/wiz-tree/wiz-tree.module';
import { WizRoot } from './components/views/wiz-root';
import { WizBehaviorDirectiveModule } from './directives/behavior/wiz-behavior-directives.module';
import { WizStylingDirectiveModule } from './directives/styling/wiz-styling-directives.module';
import { WizValidatorModule } from './directives/validators/wiz-validator.module';
import { WizItemRendererModule } from './interfaces/wiz-item-renderer';
import { WizCommandManager } from './services/core/command/wiz-command.manager';
import { WizInitGroupCommand } from './services/core/command/wiz-init-group-command';
import { WizComponentManager } from './services/core/component/wiz-component.manager';
import { WizNavigationManager } from './services/core/navigation/wiz-navigation.manager';
import { PopUpManager } from './services/core/popup/popup.manager';
import { ConfirmationPopRenderer } from './components/popup/confirmation/confirmation-pop.renderer';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { HammerModule } from '@angular/platform-browser';
import { CollectionModule } from './services/collections/collection.module';
import { ToastRenderer } from './components/popup/wiz-toast/toast.renderer';
import { ToastManager } from './services/core/popup/toast.manager';
import { FilterComponent } from './components/filter/filter.component';
import { WizInputModule } from './components/controls/input/wiz-input.module';
import { NgxLoadingComponent } from './components/views/lock/ngx-loading.component';
import { FormsModule } from '@angular/forms';
import { AngularEditorModule   } from '@wfpena/angular-wysiwyg';

/**
 * Application initialization method.
 * Uses the init group command injected by he application or a default group command if not set
 * @param injector Injector
 */
export function wizInitApp(injector: Injector): () => Promise<any> {
  let wizInitGroupCmd: WizInitGroupCommand = injector.get(
    WizInitGroupCommand,
    null
  );
  // console.log(wizInitGroupCmd);
  if (!wizInitGroupCmd) {
    // Default init group command with english translation loading
    wizInitGroupCmd = new WizInitGroupCommand();
  }

  return () =>
    new Promise<void>((resolve) => {
      wizInitGroupCmd.attach((state) => resolve());
      wizInitGroupCmd.execute();
    });
}

// Module de présentation statique des services bas niveau
@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA], // schema custom pour import des selectors dans les subLibs
  declarations: [
    WizRoot,
    ConfirmationPopRenderer,
    ToastRenderer,
    NgxLoadingComponent,
    FilterComponent,
  ],
  imports: [
    FormsModule,
    CommonModule,
    MatIconModule,
    WizDateModule,
    WizDropdownModule,
    WizLabelModule,
    WizImageModule,
    WizInputModule,
    WizButtonModule,
    WizLayoutModule,
    WizStylingDirectiveModule,
    WizTreeModule,
    WizBehaviorDirectiveModule,
    WizItemRendererModule,
    WizValidatorModule,
    WizListModule,
    CollectionModule,
    WizTabModule,
    WizAgGridModule,
    AngularEditorModule,
    WizIconModule,
    HammerModule, // migration angular 10, désormais angular inclut le module hammer pour la gestion des panMove
  ],
  providers: [
    // managers disponibles
    MatDialogModule,
    // WizCommandManager, WizNavigationManager, WizPopUpManager, WizEventManager, WizFocusManager,
    { provide: OverlayContainer, useClass: WizOverlayContainer }, // Overlay container custom inclus dans WizRoot
    {
      provide: APP_INITIALIZER,
      useFactory: wizInitApp,
      deps: [Injector],
      multi: true,
    },
  ],
  exports: [
    // composant disponibles
    WizRoot,
    CommonModule,
    NoopAnimationsModule,
    WizDateModule,
    WizDropdownModule,
    WizLabelModule,
    WizImageModule,
    WizInputModule,
    WizButtonModule,
    WizLayoutModule,
    MatIconModule,
    WizStylingDirectiveModule,
    WizBehaviorDirectiveModule,
    WizTreeModule,
    WizItemRendererModule,
    WizValidatorModule,
    WizListModule,
    CollectionModule,
    WizAgGridModule,
    AngularEditorModule,
    WizIconModule,
    WizTabModule,
    FilterComponent,
  ],
})
export class WizModule {
  // sur construction, alimentation des services des managers
  constructor(
    router: Router,
    httpClient: HttpClient,
    dialog: MatDialog,
    overlay: Overlay,
    compoResolver: ComponentFactoryResolver,
    injector: Injector,
    rendererFactory: RendererFactory2
  ) {
    WizNavigationManager.router = router; // alimentation du router sur navigation

    WizCommandManager.httpClient = httpClient; // alimentation du client sur remoteCall

    PopUpManager.dialog = dialog; // alimentation du gestionnaire de popUp
    ToastManager.overlay = overlay; // alimentation du gestionnaire de toasts, la couche
    ToastManager.injector = injector; // gestion des toasts, l'injecteur de contenu

    WizComponentManager.instance.resolver = compoResolver; // initiliazes the component manager
    WizComponentManager.instance.rendererFactory = rendererFactory;
  }
}
